/**
 * "Finance Details" popup opened directly from a Road/Ocean/Air shipment's
 * own edit page (CreateShipment.js / CreateOceanShipment.js /
 * CreateAirShipment.js) -- lets an operator see every finance transaction
 * tied to this file (customer invoices, supplier costs/invoices, profit)
 * without leaving the shipment screen. Built on the shared FinanceUiKit and
 * the app's own DataTable so it reads as part of the same Finance module
 * instead of a one-off design.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, IconButton, Grid, Divider, Fade } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import InboxRoundedIcon from "@mui/icons-material/InboxRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

import {
    COLORS,
    SPACE,
    FinanceCard,
    FinanceSectionHeader,
    FinanceKpiTile,
    FinanceStatusChip,
} from "./FinanceUiKit";

const API_URL = process.env.REACT_APP_API_URL;

const formatMoney = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value || 0));

const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleDateString("en-GB");
};

const EmptyState = ({ label }) => (
    <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4} gap={1}>
        <InboxRoundedIcon sx={{ fontSize: 34, color: COLORS.subtext, opacity: 0.6 }} />
        <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
            {label}
        </MDTypography>
    </MDBox>
);

/**
 * The trigger button opened from the shipment page. Was a plain
 * `variant="outlined"` MUI Button before -- on this app's light background
 * that reads as an all-white, near-invisible box. A solid gradient pill with
 * its own glow reads as a real call to action instead of blending in.
 */
export const FinanceDetailsButton = ({ onClick }) => (
    <MDBox
        component="button"
        type="button"
        onClick={onClick}
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2.75,
            py: 1.2,
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontFamily: "inherit",
            fontWeight: 700,
            fontSize: "14px",
            letterSpacing: ".01em",
            color: "#fff",
            background: `linear-gradient(135deg, ${COLORS.navy} 0%, ${COLORS.blue} 100%)`,
            boxShadow: "0 10px 24px rgba(11,31,58,0.35)",
            transition: "transform .15s ease, box-shadow .15s ease",
            "&:hover": {
                transform: "none",
                boxShadow: "0 14px 30px rgba(11,31,58,0.45)",
            },
            "&:active": {
                transform: "translateY(0)",
            },
        }}
    >
        <AccountBalanceWalletRoundedIcon sx={{ fontSize: 19 }} />
        Finance Details
    </MDBox>
);

/** Radial margin gauge -- a conic-gradient ring swept to |margin|%, green for
 * profit and red for loss, with the number in the middle. Gives the P&L
 * snapshot a focal point instead of just another row of tiles. */
const MarginGauge = ({ margin = 0 }) => {
    const positive = margin >= 0;
    const sweep = Math.max(Math.min(Math.abs(margin), 100), 0) * 3.6;
    const color = positive ? COLORS.green : COLORS.red;

    return (
        <MDBox
            width={104}
            height={104}
            minWidth={104}
            borderRadius="50%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
                background: `conic-gradient(${color} ${sweep}deg, #E4E7EC 0deg)`,
                boxShadow: "inset 0 0 0 1px rgba(16,24,40,0.04)",
            }}
        >
            <MDBox
                width={80}
                height={80}
                borderRadius="50%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ background: COLORS.white }}
            >
                {positive ? (
                    <TrendingUpRoundedIcon sx={{ fontSize: 16, color }} />
                ) : (
                    <TrendingDownRoundedIcon sx={{ fontSize: 16, color }} />
                )}
                <MDTypography variant="h6" fontWeight="bold" sx={{ color: COLORS.text, lineHeight: 1.2, mt: 0.25 }}>
                    {margin.toFixed(0)}%
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: COLORS.subtext, fontSize: "9px", letterSpacing: ".06em" }}>
                    MARGIN
                </MDTypography>
            </MDBox>
        </MDBox>
    );
};

const WaterfallRow = ({ icon, label, value, bold, color }) => (
    <MDBox display="flex" alignItems="center" justifyContent="space-between">
        <MDBox display="flex" alignItems="center" gap={1}>
            {icon}
            <MDTypography variant={bold ? "button" : "button"} fontWeight={bold ? "bold" : "regular"} sx={{ color: bold ? COLORS.text : COLORS.subtext }}>
                {label}
            </MDTypography>
        </MDBox>
        <MDTypography variant={bold ? "h6" : "button"} fontWeight="bold" sx={{ color: color || COLORS.text }}>
            {value}
        </MDTypography>
    </MDBox>
);

const ShipmentFinanceModal = ({ open, onClose, shipmentId, shipmentNo, sourceType }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);

    const fetchSummary = useCallback(async () => {
        if (!shipmentId) return;
        setLoading(true);
        setError("");
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_URL}/finance/shipment/${shipmentId}/summary`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const result = await response.json();
            if (!response.ok || !result.success) {
                throw new Error(result?.message || "Finance details could not be loaded.");
            }
            setData(result.data);
        } catch (err) {
            setError(err.message || "Finance details could not be loaded.");
        } finally {
            setLoading(false);
        }
    }, [shipmentId]);

    useEffect(() => {
        if (open) fetchSummary();
    }, [open, fetchSummary]);

    const summary = data?.summary || {};
    const currency = data?.currency || "EUR";
    const hasAnyRecord =
        (data?.customerInvoices?.length || 0) +
            (data?.supplierInvoices?.length || 0) +
            (data?.supplierCosts?.length || 0) >
        0;

    const supplierRows = useMemo(
        () => [
            ...(data?.supplierInvoices || []).map((invoice) => ({
                _id: invoice._id,
                supplier: invoice.supplierName || "—",
                item: invoice.invoiceNo || "—",
                amount: invoice.totalAmount,
                currency: invoice.currency,
                status: invoice.paymentStatus,
            })),
            ...(data?.supplierCosts || []).map((cost) => ({
                _id: cost._id,
                supplier: cost.supplierName || "—",
                item: cost.costItemName || "—",
                amount: cost.actualInvoiceAmount || cost.expectedAmount,
                currency: cost.currency,
                status: cost.invoiceStatus,
            })),
        ],
        [data]
    );

    const customerInvoiceTable = useMemo(
        () => ({
            columns: [
                { Header: "Invoice No", accessor: "invoiceNo", width: "24%" },
                {
                    Header: "Date",
                    accessor: "invoiceDate",
                    Cell: ({ row }) => formatDate(row.original.invoiceDate),
                },
                {
                    Header: "Amount",
                    accessor: "totalAmount",
                    align: "right",
                    Cell: ({ row }) => formatMoney(row.original.totalAmount, row.original.currency),
                },
                {
                    Header: "Payment",
                    accessor: "paymentStatus",
                    align: "center",
                    Cell: ({ row }) => <FinanceStatusChip status={row.original.paymentStatus} />,
                },
                {
                    Header: "EDM",
                    accessor: "edmStatus",
                    align: "center",
                    Cell: ({ row }) => (
                        <FinanceStatusChip status={row.original.edmStatus} label={row.original.edmStatus || "—"} />
                    ),
                },
            ],
            rows: data?.customerInvoices || [],
        }),
        [data]
    );

    const supplierTable = useMemo(
        () => ({
            columns: [
                { Header: "Supplier", accessor: "supplier", width: "30%" },
                { Header: "Item / Invoice No", accessor: "item" },
                {
                    Header: "Amount",
                    accessor: "amount",
                    align: "right",
                    Cell: ({ row }) => formatMoney(row.original.amount, row.original.currency),
                },
                {
                    Header: "Status",
                    accessor: "status",
                    align: "center",
                    Cell: ({ row }) => <FinanceStatusChip status={row.original.status} />,
                },
            ],
            rows: supplierRows,
        }),
        [supplierRows]
    );

    const returnTable = useMemo(
        () => ({
            columns: [
                {
                    Header: "Date",
                    accessor: "invoiceDate",
                    Cell: ({ row }) => formatDate(row.original.invoiceDate),
                },
                { Header: "Reason", accessor: "returnReason" },
                {
                    Header: "Amount",
                    accessor: "returnedAmount",
                    align: "right",
                    Cell: ({ row }) => formatMoney(row.original.returnedAmount, row.original.currency),
                },
                {
                    Header: "Status",
                    accessor: "returnStatus",
                    align: "center",
                    Cell: ({ row }) => <FinanceStatusChip status={row.original.returnStatus} />,
                },
            ],
            rows: data?.returnInvoices || [],
        }),
        [data]
    );

    const openFullFinanceJob = () => {
        const anchorInvoiceId = data?.customerInvoices?.[0]?._id;
        if (anchorInvoiceId) {
            navigate(`/invoice/finance/jobs/${anchorInvoiceId}`);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            TransitionComponent={Fade}
            PaperProps={{
                sx: {
                    borderRadius: "10px",
                    overflow: "hidden",
                    background: COLORS.page,
                },
            }}
        >
            <MDBox
                px={3.5}
                py={2.5}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    background: `linear-gradient(135deg, ${COLORS.navy}, ${COLORS.navySoft})`,
                    color: COLORS.white,
                }}
            >
                <MDBox display="flex" alignItems="center" gap={1.5}>
                    <MDBox
                        width={42}
                        height={42}
                        borderRadius="8px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ background: "rgba(255,255,255,0.12)" }}
                    >
                        <AccountBalanceWalletRoundedIcon sx={{ color: "#fff" }} />
                    </MDBox>
                    <MDBox>
                        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#fff" }}>
                            Finance Details
                        </MDTypography>
                        <MDBox display="flex" alignItems="center" gap={0.75} mt={0.25}>
                            <LocalShippingRoundedIcon sx={{ fontSize: 15, color: "rgba(255,255,255,0.7)" }} />
                            <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                                {shipmentNo || "—"}
                                {sourceType ? ` · ${String(sourceType).toUpperCase()}` : ""}
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                </MDBox>
                <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
                    <CloseRoundedIcon />
                </IconButton>
            </MDBox>

            <DialogContent sx={{ p: SPACE.sectionGap, background: COLORS.page }}>
                {loading && (
                    <MDBox display="flex" alignItems="center" justifyContent="center" py={6}>
                        <MDTypography variant="button" sx={{ color: COLORS.subtext }}>
                            Loading finance details…
                        </MDTypography>
                    </MDBox>
                )}

                {!loading && error && (
                    <MDBox display="flex" alignItems="center" justifyContent="center" py={6}>
                        <MDTypography variant="button" sx={{ color: COLORS.red }}>
                            {error}
                        </MDTypography>
                    </MDBox>
                )}

                {!loading && !error && data && (
                    <MDBox display="flex" flexDirection="column" gap={SPACE.sectionGap}>
                        {!hasAnyRecord ? (
                            <FinanceCard>
                                <EmptyState label="No invoices or supplier costs recorded for this shipment yet." />
                            </FinanceCard>
                        ) : (
                            <>
                                <FinanceCard>
                                    <MDBox
                                        p={SPACE.cardPaddingLg}
                                        display="flex"
                                        alignItems="center"
                                        gap={3}
                                        flexWrap="wrap"
                                    >
                                        <MarginGauge margin={summary.margin || 0} />
                                        <MDBox flexGrow={1} minWidth={220} display="flex" flexDirection="column" gap={1.25}>
                                            <WaterfallRow
                                                icon={<ReceiptLongRoundedIcon sx={{ fontSize: 18, color: COLORS.blue }} />}
                                                label="Revenue"
                                                value={formatMoney(summary.totalRevenue, currency)}
                                            />
                                            <WaterfallRow
                                                icon={<PaidRoundedIcon sx={{ fontSize: 18, color: COLORS.amber }} />}
                                                label="− Cost"
                                                value={formatMoney(summary.totalCost, currency)}
                                            />
                                            <Divider sx={{ my: 0 }} />
                                            <WaterfallRow
                                                icon={
                                                    summary.profit >= 0 ? (
                                                        <TrendingUpRoundedIcon sx={{ fontSize: 18, color: COLORS.green }} />
                                                    ) : (
                                                        <TrendingDownRoundedIcon sx={{ fontSize: 18, color: COLORS.red }} />
                                                    )
                                                }
                                                label="= Profit"
                                                value={formatMoney(summary.profit, currency)}
                                                bold
                                                color={summary.profit >= 0 ? COLORS.green : COLORS.red}
                                            />
                                        </MDBox>
                                    </MDBox>
                                </FinanceCard>

                                {summary.hasMixedCurrency && (
                                    <MDBox
                                        display="flex"
                                        alignItems="center"
                                        gap={1}
                                        px={2}
                                        py={1.25}
                                        borderRadius={SPACE.radiusTile}
                                        sx={{ background: COLORS.amberLight, border: `1px solid #FEDF89` }}
                                    >
                                        <WarningAmberRoundedIcon sx={{ fontSize: 18, color: COLORS.amber }} />
                                        <MDTypography variant="caption" fontWeight="bold" sx={{ color: COLORS.amber }}>
                                            Some records are in a different currency than {currency} and are excluded from the totals above — check them individually in the tables below.
                                        </MDTypography>
                                    </MDBox>
                                )}

                                <Grid container spacing={SPACE.rowGap}>
                                    <Grid item xs={6} md={3}>
                                        <FinanceKpiTile
                                            title="Invoices"
                                            value={summary.invoiceCount || 0}
                                            subtitle="Customer invoices"
                                            icon={<ReceiptLongRoundedIcon fontSize="small" />}
                                            tone="blue"
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FinanceKpiTile
                                            title="Collected"
                                            value={formatMoney(summary.totalCollected, currency)}
                                            subtitle="Received from customer"
                                            icon={<PaymentsRoundedIcon fontSize="small" />}
                                            tone="green"
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FinanceKpiTile
                                            title="Outstanding"
                                            value={formatMoney(summary.outstandingRevenue, currency)}
                                            subtitle="Not yet collected"
                                            icon={<AccountBalanceWalletRoundedIcon fontSize="small" />}
                                            tone="purple"
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <FinanceKpiTile
                                            title="Supplier Cost"
                                            value={summary.supplierInvoiceCount || 0}
                                            subtitle="Supplier invoice(s)"
                                            icon={<PaidRoundedIcon fontSize="small" />}
                                            tone="amber"
                                        />
                                    </Grid>
                                </Grid>

                                <FinanceCard>
                                    <MDBox p={SPACE.cardPadding}>
                                        <FinanceSectionHeader
                                            title="Customer Invoices"
                                            subtitle="Revenue billed for this file"
                                        />
                                        {!data.customerInvoices.length ? (
                                            <EmptyState label="No customer invoices yet." />
                                        ) : (
                                            <DataTable
                                                table={customerInvoiceTable}
                                                canSearch={false}
                                                showTotalEntries={false}
                                                entriesPerPage={{ defaultValue: 5, entries: [5, 10, 20] }}
                                                isSorted
                                                noEndBorder
                                                disableInternalSort
                                            />
                                        )}
                                    </MDBox>
                                </FinanceCard>

                                <FinanceCard>
                                    <MDBox p={SPACE.cardPadding}>
                                        <FinanceSectionHeader
                                            title="Supplier Costs"
                                            subtitle="Costs incurred/invoiced by suppliers for this file"
                                        />
                                        {!supplierRows.length ? (
                                            <EmptyState label="No supplier costs recorded yet." />
                                        ) : (
                                            <DataTable
                                                table={supplierTable}
                                                canSearch={false}
                                                showTotalEntries={false}
                                                entriesPerPage={{ defaultValue: 5, entries: [5, 10, 20] }}
                                                isSorted
                                                noEndBorder
                                                disableInternalSort
                                            />
                                        )}
                                    </MDBox>
                                </FinanceCard>

                                {data.returnInvoices.length > 0 && (
                                    <FinanceCard>
                                        <MDBox p={SPACE.cardPadding}>
                                            <FinanceSectionHeader
                                                title="Returns"
                                                subtitle="Credit notes issued against this file's invoices"
                                            />
                                            <DataTable
                                                table={returnTable}
                                                canSearch={false}
                                                showTotalEntries={false}
                                                entriesPerPage={{ defaultValue: 5, entries: [5, 10, 20] }}
                                                isSorted
                                                noEndBorder
                                                disableInternalSort
                                            />
                                        </MDBox>
                                    </FinanceCard>
                                )}

                                {data.customerInvoices.length > 0 && (
                                    <>
                                        <Divider sx={{ my: 0.5 }} />
                                        <MDBox display="flex" justifyContent="flex-end">
                                            <MDButton
                                                variant="gradient"
                                                color="dark"
                                                size="small"
                                                endIcon={<OpenInNewRoundedIcon fontSize="small" />}
                                                onClick={openFullFinanceJob}
                                            >
                                                Open Full Finance Job
                                            </MDButton>
                                        </MDBox>
                                    </>
                                )}
                            </>
                        )}
                    </MDBox>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ShipmentFinanceModal;
